<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import { onMount } from 'svelte';
  import type {
    Project,
    ProjectsByStatus,
    CalendarEvent,
    WeekPeriod,
    DisplayFilter,
  } from '../lib/types';
  import { showProjects, showEvents } from '../lib/types';
  import {
    getWeekDisplayRange,
    getWeekStart,
    getWeekEnd,
    isCurrentWeek,
    isFutureWeek,
    isPastWeek,
    categoryIcons,
    statusAccentColors,
    eventsForWeek,
    isMultiDayEvent,
    eventWeekSpan,
    eventTotalDays,
    parseCalendarDate,
    formatCalendarDate,
    progressStyle,
    progressComplete,
    sortEventsForDisplay,
    formatEventTimeShort,
  } from '../lib/utils';
  import { showProjectTooltip, hideProjectTooltip } from '../lib/stores/projectTooltip';
  import { setupMaxVisible, MAX_VISIBLE_PRESETS } from '../lib/useMaxVisible';
  import {
    draggedProject,
    draggedEvent,
    openProjectRail,
    openProjectModalForCreate,
    openEventModal,
    openEventModalForEdit,
    openEventsRailForWeek,
    requestProjectMove,
    requestEventMove,
  } from '../lib/stores/ui';
  import { showProjectCellPopover, hideProjectCellPopover } from '../lib/stores/projectCellPopover';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    visibleWeeks: string[];
    projectsByWeek: Record<string, ProjectsByStatus>;
    events: CalendarEvent[];
    currentWeek: string;
    period: WeekPeriod;
    display?: DisplayFilter | null;
  }

  let {
    visibleWeeks,
    projectsByWeek,
    events,
    currentWeek,
    period = 'month',
    display = null,
  }: Props = $props();

  // Period options for the selector
  const periodOptions: { value: WeekPeriod; label: string }[] = [
    { value: '1-week', label: '1 Week' },
    { value: 'month', label: 'Month' },
    { value: '5', label: '5 weeks' },
    { value: '7', label: '7 weeks' },
    { value: 'quarter', label: 'Quarter' },
  ];

  function setPeriod(newPeriod: WeekPeriod) {
    if (newPeriod === period) return;

    const params = new URLSearchParams();
    params.set('view', 'table');
    if (newPeriod !== 'month') {
      params.set('range', newPeriod);
    }

    router.get(`/week/${currentWeek}?${params.toString()}`, {}, { preserveScroll: true });
  }

  // Get all projects for a week as a flat list (ordered: active, planned, done)
  function getAllProjects(weekKey: string): Project[] {
    const data = projectsByWeek[weekKey] || { planned: [], active: [], almost: [], done: [] };
    return [...(data.active || []), ...(data.planned || []), ...(data.done || [])];
  }

  // Get events for a week
  function getWeekEvents(weekKey: string): CalendarEvent[] {
    const weekStart = getWeekStart(weekKey);
    const weekEnd = getWeekEnd(weekKey);

    return eventsForWeek(events, weekKey);
  }

  // Determine week state
  function getWeekState(weekKey: string): 'past' | 'current' | 'future' {
    if (isCurrentWeek(weekKey)) return 'current';
    if (isPastWeek(weekKey)) return 'past';
    return 'future';
  }

  function navigateToWeek(weekKey: string) {
    router.get(`/week/${weekKey}?view=week-focus`, {}, { preserveScroll: true });
  }

  // Project cell popover handlers
  function handleMoreChipEnter(e: MouseEvent, weekKey: string, projects: Project[]) {
    showProjectCellPopover(projects, getWeekDisplayRange(weekKey), e.currentTarget as HTMLElement);
  }

  function handleMoreChipLeave() {
    hideProjectCellPopover();
  }

  // Dynamic max items + responsive column count
  let weekGrid: HTMLElement;
  let maxVisibleProjects = $state<number>(MAX_VISIBLE_PRESETS.projectChipGrid.defaultVisible);
  let maxVisibleEvents = $state<number>(MAX_VISIBLE_PRESETS.eventChipGrid.defaultVisible);
  let projectColumns = $state<number>(4);
  let eventColumns = $state<number>(4);

  const MIN_COL_WIDTH = 100; // px per grid column before reducing count
  let projectMaxVisibleCleanup: (() => void) | null = null;
  let eventMaxVisibleCleanup: (() => void) | null = null;

  function setupProjectMaxVisible(container: HTMLElement, columns: number) {
    if (projectMaxVisibleCleanup) {
      projectMaxVisibleCleanup();
      projectMaxVisibleCleanup = null;
    }
    projectMaxVisibleCleanup = setupMaxVisible(
      container,
      {
        ...MAX_VISIBLE_PRESETS.projectChipGrid,
        columns,
      },
      (max) => {
        maxVisibleProjects = max;
      },
    );
  }

  function setupEventMaxVisible(container: HTMLElement, columns: number) {
    if (eventMaxVisibleCleanup) {
      eventMaxVisibleCleanup();
      eventMaxVisibleCleanup = null;
    }
    eventMaxVisibleCleanup = setupMaxVisible(
      container,
      {
        ...MAX_VISIBLE_PRESETS.eventChipGrid,
        columns,
      },
      (max) => {
        maxVisibleEvents = max;
      },
    );
  }

  onMount(() => {
    const cleanups: (() => void)[] = [];

    // Find the first row's containers for measurement
    const projectsGrid = weekGrid?.querySelector('.items-grid') as HTMLElement;
    const eventsGrid = weekGrid?.querySelectorAll('.items-grid')[1] as HTMLElement;

    // Responsive column count + max visible for projects
    if (projectsGrid) {
      const colObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const w = projectsGrid.clientWidth;
          const newCols = Math.max(1, Math.min(4, Math.floor(w / MIN_COL_WIDTH)));
          if (newCols !== projectColumns) {
            projectColumns = newCols;
            setupProjectMaxVisible(projectsGrid, newCols);
          }
        });
      });
      colObserver.observe(projectsGrid);
      cleanups.push(() => colObserver.disconnect());

      setupProjectMaxVisible(projectsGrid, projectColumns);
    }

    // Responsive column count + max visible for events
    if (eventsGrid) {
      const colObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const w = eventsGrid.clientWidth;
          const newCols = Math.max(1, Math.min(4, Math.floor(w / MIN_COL_WIDTH)));
          if (newCols !== eventColumns) {
            eventColumns = newCols;
            setupEventMaxVisible(eventsGrid, newCols);
          }
        });
      });
      colObserver.observe(eventsGrid);
      cleanups.push(() => colObserver.disconnect());

      setupEventMaxVisible(eventsGrid, eventColumns);
    }

    return () => {
      cleanups.forEach((fn) => fn());
      if (projectMaxVisibleCleanup) projectMaxVisibleCleanup();
      if (eventMaxVisibleCleanup) eventMaxVisibleCleanup();
    };
  });

  // Drag and drop for projects
  let projectDropTarget = $state<string | null>(null);
  let draggingProjectId = $state<string | null>(null);

  function handleProjectDragStart(e: DragEvent, project: Project) {
    hideProjectTooltip(true);
    draggingProjectId = project.id;
    draggedProject.set(project);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/project', String(project.id));
    }
  }

  function handleProjectDragEnd() {
    draggingProjectId = null;
    draggedProject.set(null);
  }

  function handleProjectDragOver(e: DragEvent, weekKey: string) {
    e.preventDefault();
    if ($draggedProject) {
      projectDropTarget = weekKey;
    }
  }

  function handleProjectDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      projectDropTarget = null;
    }
  }

  function handleProjectDrop(e: DragEvent, weekKey: string) {
    e.preventDefault();
    projectDropTarget = null;

    // Only process if a project (not an event) is being dragged
    if (!$draggedProject) return;

    const projectId =
      e.dataTransfer?.getData('application/project') || e.dataTransfer?.getData('text/plain');
    if (projectId) {
      requestProjectMove(projectId, $draggedProject, 'week', { weekKey, status: 'planned' });
    }
  }

  // Drag and drop for events
  let eventDropTarget = $state<string | null>(null);
  let draggingEventId = $state<string | null>(null);

  function handleEventDragStart(e: DragEvent, event: CalendarEvent) {
    draggingEventId = event.id;
    draggedEvent.set(event.id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/event', String(event.id));
    }
  }

  function handleEventDragEnd() {
    draggingEventId = null;
    draggedEvent.set(null);
  }

  function handleEventDragOver(e: DragEvent, weekKey: string) {
    e.preventDefault();
    if ($draggedEvent) {
      eventDropTarget = weekKey;
    }
  }

  function handleEventDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      eventDropTarget = null;
    }
  }

  function handleEventDrop(e: DragEvent, weekKey: string) {
    e.preventDefault();
    eventDropTarget = null;

    // Only process if an event (not a project) is being dragged
    if (!$draggedEvent) return;

    const eventId =
      e.dataTransfer?.getData('application/event') || e.dataTransfer?.getData('text/plain');
    if (eventId) {
      const eventObj = events.find((candidate) => candidate.id === eventId);
      if (eventObj) {
        requestEventMove(eventId, eventObj, 'week', { weekKey });
      } else {
        router.post(
          `/calendar_events/${eventId}/move_to_week`,
          {
            week_key: weekKey,
          },
          {
            preserveScroll: true,
          },
        );
      }
    }
  }

  function formatEventDate(dateStr: string): string {
    const parts = dateStr.split('-');
    const day = parseInt(parts[2]);
    return String(day);
  }

  function formatEventDay(dateStr: string): string {
    const date = parseCalendarDate(dateStr);
    return formatCalendarDate(date, { weekday: 'short' }).slice(0, 2);
  }

  // Get the first day (Sunday) of a week for creating events
  function getWeekFirstDate(weekKey: string): string {
    const weekStart = getWeekStart(weekKey);
    return weekStart.toISOString().split('T')[0];
  }

  // Get month key from week key for project creation
  function getWeekMonthKey(weekKey: string): string {
    const weekStart = getWeekStart(weekKey);
    return weekStart.toISOString().slice(0, 7);
  }

  function handleAddProject(weekKey: string) {
    const monthKey = getWeekMonthKey(weekKey);
    const targetDate = getWeekFirstDate(weekKey);
    openProjectModalForCreate({
      schedulingState: 'scheduled',
      monthKey: monthKey,
      targetDate: targetDate,
      status: 'planned',
    });
  }

  function handleAddEvent(weekKey: string) {
    const date = getWeekFirstDate(weekKey);
    openEventModal(date);
  }

  function handleEventClick(event: CalendarEvent) {
    openEventModalForEdit(event);
  }

  // Double-click on empty area to add new item
  function handleProjectsDoubleClick(e: MouseEvent, weekKey: string) {
    const target = e.target as HTMLElement;
    // Only trigger if clicking on the container/grid background, not on items
    if (
      target.closest('.project-chip') ||
      target.closest('.more-chip') ||
      target.closest('.add-inline-btn')
    ) {
      return;
    }
    handleAddProject(weekKey);
  }

  function handleEventsDoubleClick(e: MouseEvent, weekKey: string) {
    const target = e.target as HTMLElement;
    // Only trigger if clicking on the container/grid background, not on items
    if (
      target.closest('.event-chip') ||
      target.closest('.more-chip') ||
      target.closest('.add-inline-btn')
    ) {
      return;
    }
    handleAddEvent(weekKey);
  }
</script>

<div class="table-container">
  <!-- Header row -->
  <div class="table-header">
    <div class="header-cell week-col">Week</div>
    {#if showProjects(display)}
      <div class="header-cell projects-col">📋 Projects</div>
    {/if}
    {#if showEvents(display)}
      <div class="header-cell events-col">
        <span>📅 Events</span>
        <div class="period-selector">
          {#each periodOptions as opt}
            <button
              type="button"
              class="period-btn"
              class:active={period === opt.value}
              onclick={() => setPeriod(opt.value)}
            >
              {opt.label}
            </button>
          {/each}
        </div>
      </div>
    {/if}
  </div>

  <div class="week-grid" bind:this={weekGrid}>
    {#each visibleWeeks as weekKey (weekKey)}
      {@const state = getWeekState(weekKey)}
      {@const isCurrent = state === 'current'}
      {@const isPast = state === 'past'}
      {@const isFuture = state === 'future'}
      {@const projects = getAllProjects(weekKey)}
      {@const weekEvents = getWeekEvents(weekKey)}
      {@const visibleProjects = projects.slice(0, maxVisibleProjects)}
      {@const hiddenProjectCount = Math.max(0, projects.length - maxVisibleProjects)}
      {@const visibleEvents = weekEvents.slice(0, maxVisibleEvents)}
      {@const hiddenEventCount = Math.max(0, weekEvents.length - maxVisibleEvents)}

      <div class="week-row" class:past={isPast} class:current={isCurrent} class:future={isFuture}>
        <!-- Week label -->
        <div class="week-col week-label">
          <button type="button" class="week-link" onclick={() => navigateToWeek(weekKey)}>
            <span class="week-range">{getWeekDisplayRange(weekKey)}</span>
            <span class="week-number">{weekKey.split('-W')[1]}</span>
          </button>
        </div>

        <!-- Projects column (drop target) -->
        {#if showProjects(display)}
          <div
            class="projects-col"
            class:drop-target={projectDropTarget === weekKey}
            role="group"
            ondragover={(e) => handleProjectDragOver(e, weekKey)}
            ondragleave={handleProjectDragLeave}
            ondrop={(e) => handleProjectDrop(e, weekKey)}
            ondblclick={(e) => handleProjectsDoubleClick(e, weekKey)}
          >
            <div class="items-grid" style="grid-template-columns: repeat({projectColumns}, 1fr);">
              {#each visibleProjects as project (project.id)}
                <button
                  type="button"
                  class="project-chip {statusAccentColors[project.status]}"
                  style={progressStyle(project)}
                  data-progress-complete={progressComplete(project) || undefined}
                  class:dragging={draggingProjectId === project.id}
                  draggable={!$viewport.isMobile}
                  ondragstart={(e) => handleProjectDragStart(e, project)}
                  ondragend={handleProjectDragEnd}
                  onclick={() => {
                    hideProjectTooltip(true);
                    openProjectRail(project.id);
                  }}
                  onmouseenter={(e) => showProjectTooltip(project, e.currentTarget as HTMLElement)}
                  onmouseleave={() => hideProjectTooltip()}
                  title="{project.title} ({project.status}){project.multiWeek
                    ? ` — Week ${project.weekIndex} of ${project.totalWeeks}`
                    : ''}"
                >
                  <span class="chip-category">{categoryIcons[project.category]}</span>
                  {#if project.isPriority}
                    <span class="chip-star">★</span>
                  {/if}
                  <span class="chip-title">{project.title}</span>
                  {#if project.multiWeek && project.weekIndex && project.totalWeeks}
                    <span class="chip-span">{project.weekIndex}/{project.totalWeeks}</span>
                  {/if}
                </button>
              {/each}
              {#if hiddenProjectCount > 0}
                <span
                  class="more-chip"
                  role="note"
                  onmouseenter={(e) => handleMoreChipEnter(e, weekKey, projects)}
                  onmouseleave={handleMoreChipLeave}
                >
                  +{hiddenProjectCount} more
                </span>
              {/if}
              {#if projects.length === 0}
                <span class="empty-cell">—</span>
              {/if}
            </div>
            <button
              type="button"
              class="add-inline-btn hover-reveal"
              onclick={() => handleAddProject(weekKey)}
              title="Add project"
            >
              +
            </button>
          </div>
        {/if}

        <!-- Events column (drop target) -->
        {#if showEvents(display)}
          <div
            class="events-col"
            class:drop-target={eventDropTarget === weekKey}
            role="group"
            ondragover={(e) => handleEventDragOver(e, weekKey)}
            ondragleave={handleEventDragLeave}
            ondrop={(e) => handleEventDrop(e, weekKey)}
            ondblclick={(e) => handleEventsDoubleClick(e, weekKey)}
          >
            <div class="items-grid" style="grid-template-columns: repeat({eventColumns}, 1fr);">
              {#each visibleEvents as event (event.id)}
                {@const timeStr = formatEventTimeShort(event)}
                <button
                  type="button"
                  class="event-chip event-{event.type}"
                  class:dragging={draggingEventId === event.id}
                  draggable={!$viewport.isMobile}
                  ondragstart={(e) => handleEventDragStart(e, event)}
                  ondragend={handleEventDragEnd}
                  onclick={() => handleEventClick(event)}
                  title="{timeStr ? `${timeStr} ` : ''}{event.title} ({event.date})"
                >
                  <span class="event-day">{formatEventDay(event.date)}</span>
                  <span class="event-date"
                    >{formatEventDate(event.date)}{#if timeStr}
                      · {timeStr}{/if}</span
                  >
                  <span class="chip-title">{event.title}</span>
                  {#if isMultiDayEvent(event)}
                    {#if eventWeekSpan(event, weekKey).totalWeeks > 1}
                      <span class="chip-span"
                        >{eventWeekSpan(event, weekKey).weekIndex}/{eventWeekSpan(event, weekKey)
                          .totalWeeks}</span
                      >
                    {:else}
                      <span class="chip-span">{eventTotalDays(event)}d</span>
                    {/if}
                  {/if}
                </button>
              {/each}
              {#if hiddenEventCount > 0}
                <button
                  type="button"
                  class="more-chip"
                  onclick={() => openEventsRailForWeek(weekKey, getWeekDisplayRange(weekKey))}
                >
                  +{hiddenEventCount} more
                </button>
              {/if}
              {#if weekEvents.length === 0}
                <span class="empty-cell">—</span>
              {/if}
            </div>
            <button
              type="button"
              class="add-inline-btn hover-reveal"
              onclick={() => handleAddEvent(weekKey)}
              title="Add event"
            >
              +
            </button>
          </div>
        {/if}
      </div>
    {/each}
  </div>
</div>

<style>
  .table-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0.375rem;
    background: #f9fafb;
  }

  /* Header row */
  .table-header {
    display: flex;
    align-items: stretch;
    background: white;
    border-radius: 0.375rem 0.375rem 0 0;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .header-cell {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  .header-cell.week-col {
    justify-content: center;
  }

  .header-cell.events-col {
    justify-content: space-between;
  }

  /* Period selector (inline in header) */
  .period-selector {
    display: flex;
    background: #f9fafb;
    border-radius: 0.25rem;
    padding: 0.125rem;
    border: 1px solid #e5e7eb;
  }

  .period-btn {
    padding: 0.125rem 0.375rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
    text-transform: none;
    letter-spacing: 0;
  }

  .period-btn:hover {
    color: #374151;
    background: white;
  }

  .period-btn.active {
    color: #1d4ed8;
    background: white;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  /* Shared column widths */
  .week-col {
    width: 5.5rem;
    flex-shrink: 0;
    border-right: 1px solid #e5e7eb;
  }

  .projects-col {
    flex: 1;
    border-right: 1px dashed #e5e7eb;
    min-width: 0;
  }

  .events-col {
    flex: 1;
    min-width: 0;
  }

  .week-grid {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0 0 0.375rem 0.375rem;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .week-row {
    flex: 1;
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid #f3f4f6;
    min-height: 0;
    transition: all 0.15s;
  }

  .week-row:last-child {
    border-bottom: none;
  }

  .week-row.past {
    background: #fafafa;
  }

  .week-row.current {
    background: #eff6ff;
  }

  .week-row.future {
    background: #fefefe;
  }

  /* Drop target highlight on projects column */
  .projects-col.drop-target {
    background: #dbeafe !important;
    box-shadow: inset 0 0 0 2px #3b82f6;
    border-radius: 0.25rem;
  }

  /* Drop target highlight on events column */
  .events-col.drop-target {
    background: #dbeafe !important;
    box-shadow: inset 0 0 0 2px #3b82f6;
    border-radius: 0.25rem;
  }

  /* Week label column */
  .week-label {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.02);
  }

  .week-link {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.0625rem;
    padding: 0.25rem 0.375rem;
    background: none;
    border: none;
    cursor: pointer;
    border-radius: 0.25rem;
    transition: background 0.15s;
  }

  .week-link:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  .week-range {
    font-size: 0.625rem;
    font-weight: 600;
    color: #374151;
    letter-spacing: -0.01em;
    white-space: nowrap;
  }

  .week-number {
    font-size: 0.5rem;
    font-weight: 500;
    color: #9ca3af;
  }

  /* Projects and Events columns in data rows */
  .week-row .projects-col,
  .week-row .events-col {
    display: flex;
    flex-direction: column;
    padding: 0.25rem;
    min-height: 0;
    overflow: hidden;
  }

  /* Items grid - responsive columns via inline style */
  .items-grid {
    display: grid;
    /* grid-template-columns set via inline style for responsive column count */
    grid-auto-rows: min-content;
    gap: 0.25rem;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    align-content: start;
  }

  /* Project chip */
  .project-chip {
    display: flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.125rem 0.25rem;
    border-radius: 0.1875rem;
    font-size: 0.5625rem;
    background: #f9fafb;
    border: 1.5px solid var(--chip-border-color, #e5e7eb);
    cursor: grab;
    text-align: left;
    transition: all 0.15s;
    min-width: 0;
    overflow: hidden;
    flex-shrink: 0;
  }

  .chip-category {
    font-size: 0.5rem;
    flex-shrink: 0;
    line-height: 1;
  }

  .project-chip:hover {
    background: #f3f4f6;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  .project-chip.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .chip-star {
    color: #facc15;
    font-size: 0.5rem;
    flex-shrink: 0;
  }

  .chip-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #374151;
    min-width: 0;
  }

  .chip-span {
    flex-shrink: 0;
    font-size: 0.5rem;
    font-weight: 600;
    font-family: ui-monospace, monospace;
    color: #1d4ed8;
    background: rgba(59, 130, 246, 0.15);
    padding: 0 0.1875rem;
    border-radius: 0.125rem;
    letter-spacing: -0.02em;
  }

  /* Event chip - left accent bar style */
  .event-chip {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.1875rem 0.375rem;
    padding-left: 0.5rem;
    border-radius: 0.125rem;
    font-size: 0.5625rem;
    min-width: 0;
    overflow: hidden;
    flex-shrink: 0;
    background: #f3f4f6;
  }

  .event-chip::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #9ca3af;
    border-radius: 0.125rem 0 0 0.125rem;
  }

  /* Event type accent colors */
  .event-chip.event-appointment::before {
    background: #60a5fa;
  }

  .event-chip.event-travel::before {
    background: #a78bfa;
  }

  .event-chip.event-event::before {
    background: #4ade80;
  }

  .event-chip.event-deadline::before {
    background: #f87171;
  }

  .event-day {
    font-weight: 500;
    font-size: 0.5rem;
    color: #9ca3af;
    flex-shrink: 0;
  }

  .event-date {
    font-weight: 600;
    flex-shrink: 0;
    color: #6b7280;
  }

  /* More chip — inline in grid, same size as project chips */
  .more-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.125rem 0.25rem;
    font-size: 0.5rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: 1px dashed #9ca3af;
    border-radius: 0.1875rem;
    cursor: default;
    transition: all 0.15s;
    white-space: nowrap;
    flex-shrink: 0;
    min-width: 0;
    overflow: hidden;
  }

  .more-chip:hover {
    border-color: #3b82f6;
    color: #3b82f6;
  }

  /* Empty cell */
  .empty-cell {
    font-size: 0.5rem;
    color: #d1d5db;
    padding: 0.125rem;
  }

  /* Inline add button (shown on hover) */
  .add-inline-btn {
    position: absolute;
    top: 0.125rem;
    right: 0.125rem;
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    font-weight: 600;
    color: #9ca3af;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    opacity: 0;
  }

  .week-row:hover .add-inline-btn {
    opacity: 1;
  }

  .add-inline-btn:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #eff6ff;
  }

  /* Make columns position relative for inline add button */
  .week-row .projects-col,
  .week-row .events-col {
    position: relative;
  }

  /* Event chip as draggable button */
  .event-chip {
    cursor: grab;
    border: none;
    text-align: left;
  }

  .event-chip:hover {
    filter: brightness(0.95);
  }

  .event-chip.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }
</style>
